import { AcceleratorDocMetadata } from "../models/AcceleratorDocMetadata";
import { ColumnConfig } from "../models/config/ColumnConfig";
import { DocFieldConfig } from "../models/config/DocFieldConfig";
import { TableConfig } from "../models/config/TableConfig";
import { ExtractedFieldData } from "../models/extraction/ExtractedFieldData";
import { ExtractedTableData } from "../models/extraction/ExtractedTableData";
import {
  DlfMatcher,
  DlfMatcherProperties,
} from "../models/matchers/DlfMatcher";
import { DocumentStatus } from "../types/DocumentStatuses";
import { FieldStatus } from "../types/FieldStatuses";
import { TemplateType } from "../types/TemplateType";
import { isBodyRow } from "../types/RowType";

export {
  AcceleratorDocStatusCalculator,
  doTablesHaveOcrWarnings,
  doesTableHaveOcrWarnings,
  doesDlfHaveOcrWarnings,
  doDlfsHaveOcrWarnings,
};

type DocStatusCount = { [x in DocumentStatus]: number };
class AcceleratorDocStatusCalculator {
  // TODO: test
  public static sumDocStatuses(
    metadata: AcceleratorDocMetadata[],
  ): DocStatusCount {
    const documentStatuses = metadata.reduce(
      (prev, curr) => {
        for (const status of curr.statuses) {
          //console.debug(`${getObjectStoreIdString(curr)} has Status:${status}`);
          prev[status]++;
        }
        return prev;
      },
      {
        EMPTY: 0,
        ERROR: 0,
        UNKNOWN: 0,
        GENERAL: 0,
        GENERAL_FORMAT: 0,
        MULTIPLE: 0,
        INVALID: 0,
        OCR_WARNING: 0,
      },
    );

    return documentStatuses;
  }

  public static calculateDocumentStatus(data: {
    isTemplateType: TemplateType;
    unknown: boolean;
    wasTemplateType: TemplateType;
    hasMultiple: boolean;
    docTypeEfds: ExtractedFieldData[];
    docTypeEtds: ExtractedTableData[];
  }): DocumentStatus[] {
    const newDocStatuses: DocumentStatus[] =
      AcceleratorDocStatusCalculator.calculateStatusesFromModel(
        data.isTemplateType,
        data.unknown,
        data.wasTemplateType,
        data.hasMultiple,
      );

    newDocStatuses.push(
      ...AcceleratorDocStatusCalculator.calculateExtractionBasedStatuses(
        data.docTypeEfds,
        data.docTypeEtds,
      ),
    );

    return newDocStatuses;
  }

  public static calculateDocumentStatus_lazy(data: {
    isTemplateType: TemplateType;
    unknown: boolean;
    wasTemplateType: TemplateType;
    hasMultiple: boolean;
    hasError: boolean;
    hasEmpty: boolean;
    hasInvalid: boolean;
    hasOcrWarning: boolean;
  }): DocumentStatus[] {
    const newDocStatuses: DocumentStatus[] =
      AcceleratorDocStatusCalculator.calculateStatusesFromModel(
        data.isTemplateType,
        data.unknown,
        data.wasTemplateType,
        data.hasMultiple,
      );

    if (data.hasEmpty) {
      newDocStatuses.push("EMPTY");
    }
    if (data.hasError) {
      newDocStatuses.push("ERROR");
    }
    if (data.hasInvalid) {
      newDocStatuses.push("INVALID");
    }
    if (data.hasOcrWarning) {
      newDocStatuses.push("OCR_WARNING");
    }

    return Array.from(new Set(newDocStatuses).values());
  }

  private static calculateStatusesFromModel(
    isTemplateType: TemplateType,
    unknown: boolean,
    wasTemplateType: TemplateType,
    hasMultiple: boolean,
  ): DocumentStatus[] {
    const newDocStatuses: DocumentStatus[] = [];
    // MULTIPLE status gets removed during Reprocessing only
    if (hasMultiple) {
      newDocStatuses.push("MULTIPLE");
    }
    if (isTemplateType === "GENERAL") {
      newDocStatuses.push("GENERAL");
    } else if (isTemplateType === "FORMAT") {
      newDocStatuses.push("GENERAL_FORMAT");
    }
    // if current model unknown and but doc was classified as general, show GENERAL status ( even if model isn't
    // general! )
    if (unknown && wasTemplateType === "GENERAL") {
      newDocStatuses.push("GENERAL");
    } else if (unknown && wasTemplateType === "FORMAT") {
      newDocStatuses.push("GENERAL_FORMAT");
    }
    // if currently unknown, and was not general, use UNKNOWN status
    if (
      unknown &&
      (wasTemplateType === "NORMAL" || wasTemplateType === "UNKNOWN")
    ) {
      newDocStatuses.push("UNKNOWN");
    }
    return newDocStatuses;
  }

  private static calculateExtractionBasedStatuses(
    docTypeEfds: ExtractedFieldData[],
    docTypeEtds: ExtractedTableData[],
  ): DocumentStatus[] {
    const newDocStatuses: DocumentStatus[] = [];
    docTypeEfds.forEach((value) => {
      // do not check unextracteed fields
      if (value.getAtIndex() == undefined) {
        return;
      }
      switch (value.getAtIndex()!.fieldStatus) {
        case "ERROR":
        case "EMPTY": {
          newDocStatuses.push(
            value.getAtIndex()!.fieldStatus as DocumentStatus,
          );
        }
      }
      // Mark document INVALID if DLF is invalid
      if (!value.getAtIndex()!.valid) {
        newDocStatuses.push("INVALID");
      }
    });
    docTypeEtds.forEach((value) => {
      // do not check unextracteed fields
      if (!value.extraction) {
        return;
      }
      switch (value.extraction.status) {
        case "ERROR":
        case "EMPTY": {
          newDocStatuses.push(value.extraction.status);
        }
      }
      // Mark document INVALID if any cells are invalid
      for (const row of value.extraction.rows) {
        if (row.cells.find((cell) => !cell.valid)) {
          newDocStatuses.push("INVALID");
        }
      }
    });
    return newDocStatuses;
  }
}

function doDlfsHaveOcrWarnings(
  acceleratorOcrThresholds: boolean,
  docTypeEtds: ExtractedFieldData[],
  fields: DocFieldConfig[],
): boolean {
  let anyFailed = false;
  for (const value of docTypeEtds) {
    const fieldConfig = fields.find((tc) => tc.fieldName === value.getName());
    // do not check extracted tables without config
    if (fieldConfig == undefined) {
      continue;
    }
    if (doesDlfHaveOcrWarnings(acceleratorOcrThresholds, value, fieldConfig)) {
      anyFailed = true;
    }
  }
  return anyFailed;
}

function doTablesHaveOcrWarnings(
  useAcceleratorOcr: boolean,
  docTypeEtds: ExtractedTableData[],
  tableConfigs: TableConfig[],
): boolean {
  if (!useAcceleratorOcr) {
    return false;
  }
  let anyFailed = false;
  for (const value of docTypeEtds) {
    const tableConfig = tableConfigs.find((tc) => tc.name === value.getName());
    // do not check extracted tables without config
    if (tableConfig == undefined) {
      continue;
    }
    if (doesTableHaveOcrWarnings(useAcceleratorOcr, value, tableConfig)) {
      anyFailed = true;
    }
  }
  return anyFailed;
}

/**
 *
 * @param value
 * @param tableConfig
 * @returns true if table has OCR warnings
 */
function doesTableHaveOcrWarnings(
  useAcceleratorOcr: boolean,
  value: ExtractedTableData,
  tableConfig: TableConfig,
): boolean {
  if (!useAcceleratorOcr) {
    return false;
  }
  // do not check unextracted tables
  if (value.debug_GetExtracted() == undefined) {
    return false;
  }
  // Mark document OCR_WARNING if any cells are below threshold
  let anyFailed = false;
  for (const row of value.debug_GetExtracted()!.rows) {
    if (!isBodyRow(row)) {
      continue;
    }
    // Check template OCR confidence threhsold is greater than or equal to temlate threshold
    for (const cell of row.cells) {
      const columnConfig = tableConfig.getDocFieldConfig(cell.fieldName);
      // Skip missing ColumnConfigs
      if (!columnConfig) {
        continue;
      }
      const cellMatcher = value
        .debug_GetExtracted()!
        .tableTemplateData.columns.find(
          (colMatcher) => colMatcher.columnName === cell.fieldName,
        );
      cell.hasOcrWarning = isOcrBelowThreshold(
        cell.ocrConfidence,
        cell.fieldValue,
        cell.fieldStatus,
        cellMatcher?.matcher,
        columnConfig,
        cell.spanIds,
      );
      if (cell.hasOcrWarning) {
        anyFailed = true;
      }
    }
  }
  value.debug_GetExtracted()!.hasOcrWarning = anyFailed;
  return anyFailed;
}

/**
 *
 * @param value Extracted Field Data
 * @param docFieldConfig DocFieldConfig
 * @returns true if table has OCR warnings
 */
function doesDlfHaveOcrWarnings(
  acceleratorOcrThresholds: boolean,
  value: ExtractedFieldData,
  docFieldConfig: DocFieldConfig,
): boolean {
  if (!acceleratorOcrThresholds) {
    return false;
  }
  // do not check unextracted tables
  if (value.getAtIndex() == undefined) {
    return false;
  }
  // Mark document OCR_WARNING if any cells are below threshold
  // for (const row of value.extraction) {
  // Check template OCR confidence threhsold is greater than or equal to temlate threshold
  // for (const cell of row.cells) {
  const fieldMatcher = value.extraction?.matcherData;
  value.getAtIndex()!.hasOcrWarning = isOcrBelowThreshold(
    value.getAtIndex()!.ocrConfidence,
    value.getAtIndex()!.fieldValue,
    value.getAtIndex()!.fieldStatus,
    fieldMatcher,
    docFieldConfig,
    value.getAtIndex()!.spanIds,
  );
  return value.getAtIndex()!.hasOcrWarning;
}

/** Return true if OCR Confidence is less than threshold. Does not check OCR threshold for EMPTY,HARDCODE,BLANK dlfs */
function isOcrBelowThreshold(
  ocrConfidence: number,
  fieldValue: string,
  fieldStatus: FieldStatus,
  fieldMatcher: DlfMatcherProperties | undefined,
  fieldconfig: DocFieldConfig | ColumnConfig,
  spanIds?: string[],
): boolean {
  // do not check OCR confidence for empty fieldss
  switch (fieldStatus) {
    case "ERROR": {
      // Do not check OCR confidence for fields in ERROR with no extracted value
      if (fieldValue.length === 0) {
        return false;
      }
      // If a Cell or DLF is in ERROR, only check for OCR confidence if at least one span was captured
      const hasSpans = spanIds != undefined && spanIds.length > 0;
      if (!hasSpans) {
        return false;
      }
      break;
    }
    case "HARDCODE": {
      return false;
    }
    case "EMPTY": {
      return false;
    }
    case "BLANK": {
      return false;
    }
  }
  let actualThreshold = -1;
  // use Config OCR Confidence Threshold if Matcher has -1 threshold
  if (fieldMatcher == undefined || fieldMatcher.ocrConfidenceThreshold == -1) {
    const configOcrThresh = fieldconfig?.ocrConfidenceThreshold;
    actualThreshold = configOcrThresh ?? -1;
  } else {
    actualThreshold = fieldMatcher.ocrConfidenceThreshold;
  }
  // console.debug(`${fieldconfig.fieldName} has ocr.conf=${ocrConfidence}, threhsold=${actualThreshold}`)
  if (actualThreshold === -1) {
    return false;
  }
  // compare extracted OCR to ColumnConfig/ColumnMatcher threshold, return true if below threshold
  return ocrConfidence < actualThreshold;
}
